/* Shared header, accessible dialogs and per-history-entry product return position. */
(() => {
  if (window.ZdmSharedShell) { window.ZdmSharedShell.init(); return; }
  const bindings = new WeakMap();
  function mount(root) {
    if (bindings.has(root)) return;
    const dialog = root.querySelector('.zdm-shared-dialog');
    if (!dialog) return;
    const controller = new AbortController();
    bindings.set(root, controller);
    const opts = { signal: controller.signal };
    const scroller = root.querySelector('[data-zdm-scroll]');
    const top = root.querySelector('[data-zdm-top]');
    const header = root.querySelector('.zdm-shared-header');
    const lockTarget = scroller || document.documentElement;
    const position = () => scroller ? scroller.scrollTop : window.scrollY;
    const scrollTarget = scroller || window;
    let opener, oldOverflow, headerHeight = 44, hiddenHeader = false;
    let lastY = position(), scrollFrame = 0, viewportFrame = 0, restoreFrame = 0;
    function setHeaderHidden(hidden) {
      hiddenHeader = hidden;
      root.classList.toggle('zdm-header-hidden', hidden);
      root.style.setProperty('--zdm-visible-header-height', (hidden ? 0 : headerHeight) + 'px');
    }
    const observer = new ResizeObserver(() => {
      headerHeight = header.offsetHeight;
      root.style.setProperty('--zdm-header-height', headerHeight + 'px');
      setHeaderHidden(hiddenHeader);
    });
    if (header) observer.observe(header);
    const titles = {search:'搜索亚马逊优惠',more:'更多服务',wechat:'加入折扣群',tutorial:'折扣码教程',business:'商务合作'};
    function syncViewport() {
      viewportFrame = 0;
      if (!dialog.open) return;
      const view = window.visualViewport;
      const height = view ? view.height : window.innerHeight;
      const offset = view ? view.offsetTop : 0;
      dialog.style.setProperty('--zdm-visual-height', height + 'px');
      dialog.style.setProperty('--zdm-visual-top', offset + 'px');
      dialog.style.setProperty('--zdm-keyboard-inset', Math.max(0, window.innerHeight - height - offset) + 'px');
    }
    function requestViewport() {
      if (!viewportFrame) viewportFrame = requestAnimationFrame(syncViewport);
    }
    window.visualViewport?.addEventListener('resize', requestViewport, opts);
    window.visualViewport?.addEventListener('scroll', requestViewport, opts);
    window.addEventListener('resize', requestViewport, opts);
    function open(panel, button) {
      if (!titles[panel]) return;
      if (!dialog.open) opener = button;
      dialog.querySelectorAll('[data-zdm-panel]').forEach(p => p.hidden = p.dataset.zdmPanel !== panel);
      dialog.querySelector('h2').textContent = titles[panel];
      dialog.classList.toggle('is-search', panel === 'search');
      if (!dialog.open) {
        oldOverflow = lockTarget.style.overflowY;
        lockTarget.style.overflowY = 'hidden';
        dialog.showModal();
      }
      syncViewport();
      const focusTarget = panel === 'search' ? dialog.querySelector('input[type=search]') : dialog.querySelector('[data-zdm-close]');
      focusTarget?.focus({preventScroll:true});
      requestViewport();
    }
    root.addEventListener('click', async e => {
      const button = e.target.closest('button');
      if (!button || !root.contains(button)) return;
      if (button.dataset.zdmOpen) open(button.dataset.zdmOpen, button);
      if (button.hasAttribute('data-zdm-close')) dialog.close();
      if (button.hasAttribute('data-zdm-top')) {
        setHeaderHidden(false);
        scrollTarget.scrollTo({top:0,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
      }
      if (button.dataset.zdmCopy) {
        const status = dialog.querySelector('[data-zdm-copy-status]');
        try {
          await navigator.clipboard.writeText(button.dataset.zdmCopy);
          status.textContent='微信号已复制，请打开微信添加。';
        } catch (_) { status.textContent='未能自动复制，请长按上方微信号手动复制。'; }
      }
    }, opts);
    header?.addEventListener('focusin', () => setHeaderHidden(false), opts);
    dialog.addEventListener('click', e => {
      const r=dialog.getBoundingClientRect();
      if(e.target===dialog && (e.clientX<r.left || e.clientX>r.right || e.clientY<r.top || e.clientY>r.bottom)) dialog.close();
    }, opts);
    dialog.addEventListener('close', () => {
      lockTarget.style.overflowY = oldOverflow || '';
      dialog.querySelector('input[type=search]')?.blur();
      opener?.focus({preventScroll:true});
      lastY = position();
    }, opts);
    function update() {
      scrollFrame = 0;
      const y = Math.max(0, position());
      if (top) top.hidden = y < 300;
      if (!dialog.open) {
        if (y < 65) setHeaderHidden(false);
        else if (Math.abs(y-lastY)>8) setHeaderHidden(y>lastY);
      }
      if (Math.abs(y-lastY)>8 || y<65) lastY = y;
    }
    scrollTarget.addEventListener('scroll', () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(update);
    }, {...opts,passive:true});
    update();

    // Save the clicked card against this history entry, not a global page scroll position.
    // The catalog restores its own category/group before this handler restores the card offset.
    if (root.classList.contains('zdm-shared-deals-page')) {
      root.addEventListener('click', e => {
        const card = e.target.closest('a.ad-card');
        if (!card || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
        try {
          history.replaceState({...history.state,zdmProductReturn:{
            path:location.pathname,href:card.href,y:position(),offset:card.getBoundingClientRect().top
          }},'');
        } catch (_) {}
      }, {...opts,capture:true});
      let restoreCancelled = false;
      const cancelRestore = () => {restoreCancelled=true;cancelAnimationFrame(restoreFrame);};
      ['touchstart','pointerdown','wheel','keydown'].forEach(type=>window.addEventListener(type,cancelRestore,{...opts,passive:true}));
      function restorePosition() {
        const saved = history.state?.zdmProductReturn;
        if (!saved || saved.path !== location.pathname || restoreCancelled) return;
        cancelAnimationFrame(restoreFrame);
        restoreFrame=requestAnimationFrame(()=>{
          restoreFrame=requestAnimationFrame(()=>{
            if (restoreCancelled) return;
            const card=Array.from(root.querySelectorAll('a.ad-card')).find(a=>a.href===saved.href);
            const y=card ? position()+card.getBoundingClientRect().top-saved.offset : saved.y;
            scrollTarget.scrollTo({top:Math.max(0,y),behavior:'instant'});
            lastY=position();
            setHeaderHidden(lastY>65);
            if(top)top.hidden=lastY<300;
          });
        });
      }
      const isBackForward = () => performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
      if(isBackForward()) restorePosition();
      window.addEventListener('pageshow',e=>{
        restoreCancelled=false;
        if(e.persisted || isBackForward()) restorePosition();
      },opts);
    }
    controller.signal.addEventListener('abort',()=>{
      observer.disconnect();
      [scrollFrame,viewportFrame,restoreFrame].forEach(cancelAnimationFrame);
      if(dialog.open){lockTarget.style.overflowY=oldOverflow || '';dialog.close();}
    },{once:true});
  }
  function init() {document.querySelectorAll('[data-zdm-shell]').forEach(mount);}
  window.ZdmSharedShell={init};
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',init,{once:true}); else init();
  document.addEventListener('shopify:section:load',init);
  document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-zdm-shell]').forEach(root=>{bindings.get(root)?.abort();bindings.delete(root);}));
})();
